import { Stack, Typography } from '@mui/material';
import { forwardRef } from 'react';

export const Tag = forwardRef<
  HTMLDivElement,
  {
    icon?: string;
    content: string;
    variant?: 'primary' | 'secondary' | 'success' | 'error';
    disabled?: boolean;
    noWrap?: boolean;
    onRemove?: () => void;
  }
>(
  (
    {
      icon,
      content,
      variant = 'primary',
      disabled = false,
      noWrap = false,
      onRemove,
      ...rest
    },
    ref
  ) => {
    // The status variants tint both the text and the border, so a tag reading
    // "Licensed" or "Not backed up" carries its verdict in colour rather than only
    // in the word. Disabled still wins: a greyed-out tag is not a live status.
    const statusColor =
      disabled || variant === 'primary' || variant === 'secondary'
        ? undefined
        : `var(--mui-palette-${variant}-main)`;

    // adding typography manually for cases where it's being overridden by accordionSummary
    // once fixing accordionSummary, we can stay with the typography variant (subtitle2/body2)
    const typographySx = {
      color:
        statusColor ??
        (variant === 'secondary' || disabled
          ? 'var(--mui-palette-text-secondary)'
          : 'var(--mui-palette-text-primary)'),
      fontWeight: '400 !important',
    };

    return (
      <Stack
        direction='row'
        {...rest}
        ref={ref}
        className='px-[8px] py-[3px] rounded-[var(--mui-shape-customBorderRadius-sm)] flex-inline vertical-align-middle'
        sx={{
          alignItems: 'center',
          gap: '4px',
          width: 'fit-content',
          // A no-wrap tag can only ellipsize if it is allowed to shrink below its
          // content within the container it sits in.
          minWidth: noWrap ? 0 : undefined,
          maxWidth: noWrap ? '100%' : undefined,
          ...typographySx,
          backgroundColor: disabled
            ? 'var(--mui-palette-background-disabled)'
            : 'transparent',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: disabled
            ? 'transparent'
            : (statusColor ??
              (variant === 'primary'
                ? 'var(--mui-palette-primary-main)'
                : 'var(--mui-palette-divider)')),
        }}
      >
        {icon && <i className={icon} />}
        <Typography
          noWrap={noWrap}
          sx={{
            wordBreak: noWrap ? undefined : 'break-all',
            minWidth: noWrap ? 0 : undefined,
            ...typographySx,
          }}
          variant={variant === 'secondary' ? 'body2' : 'subtitle2'}
        >
          {content}
        </Typography>
        {onRemove && (
          <Stack
            className='cursor-pointer'
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
              borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
              '&:hover': {
                backgroundColor: 'var(--mui-palette-primary-hover)',
              },
            }}
          >
            <i className='material-symbols-close-small-rounded text-base' />
          </Stack>
        )}
      </Stack>
    );
  }
);
